import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { campaignReadService } from '../services/campaign/campaignReadService';
import { campaignService } from '../services/campaignService';
import { qrCodeService } from '../services/qrCodeService';
import { useAuth } from './AuthContext';
import { CampaignWithNodeDetails } from '../services/campaign/types';

interface CampaignContextType {
  campaigns: CampaignWithNodeDetails[];
  qrCodes: any[];
  analytics: any;
  loading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  addCampaign: (campaign: CampaignWithNodeDetails) => void;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export const useCampaignContext = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('useCampaignContext must be used within a CampaignProvider');
  }
  return context;
};

// Safe accessor that returns undefined when used outside the provider.
export const useCampaignContextSafe = () => {
  return useContext(CampaignContext) as CampaignContextType | undefined;
};

interface CampaignProviderProps {
  children: ReactNode;
}

export const CampaignProvider: React.FC<CampaignProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignWithNodeDetails[]>([]);
  const [qrCodes, setQrCodes] = useState<any[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshData = useCallback(async () => {
    if (!user?.id) {
      setCampaigns([]);
      setQrCodes([]);
      setAnalytics(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {

      // PRIORITIZE SUPABASE DATA FIRST (real data from campaign saves with bridge table data)
      const campaignsResult = await campaignReadService.getCampaignsWithAdSlots(user.id);

      const qrResult = await qrCodeService.getUserQRCodes(user.id);

      if (campaignsResult.success && campaignsResult.data) {
        setCampaigns(campaignsResult.data);

        if (qrResult.success && qrResult.data) {
          setQrCodes(qrResult.data);
        } else {
          console.warn('Failed to load QR codes from Supabase:', qrResult.error);
          setQrCodes([]);
        }

        // Load analytics data (calculates scan counts from qr_scans table)
        const analyticsResult = await campaignService.getAnalyticsData(user.id);
        if (analyticsResult.error) {
          console.warn('Failed to load analytics:', analyticsResult.error);
          setAnalytics({
            totalCampaigns: campaignsResult.data.length,
            totalScans: 0,
            activeQRs: qrResult.data?.filter((qr: any) => qr.status === 'Active').length || 0
          });
        } else {
          setAnalytics(analyticsResult.analytics);
        }

        setLoading(false);
        return;
      }

      // FALLBACK to mock data if Supabase fails
      const mockCampaigns = JSON.parse(localStorage.getItem('mockCampaigns') || '[]');
      const mockQRs = JSON.parse(localStorage.getItem('mockQRCodes') || '[]');

      // Filter by user ID
      const userCampaigns = mockCampaigns.filter((c: any) => c.user_id === user.id);
      const userQRs = mockQRs.filter((qr: any) => qr.user_id === user.id);

      if (userCampaigns.length > 0) {
        setCampaigns(userCampaigns);
        setQrCodes(userQRs);
        setAnalytics({
          totalCampaigns: userCampaigns.length,
          totalScans: userQRs.reduce((sum: number, qr: any) => sum + (qr.total_scans || 0), 0),
          activeQRs: userQRs.filter((qr: any) => qr.status === 'Active').length
        });
      } else {
        setCampaigns([]);
        setQrCodes([]);
        setAnalytics(null);
      }

    } catch (err) {
      console.error('Error refreshing campaign data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  const addCampaign = useCallback((campaign: CampaignWithNodeDetails) => {
    setCampaigns((prev: CampaignWithNodeDetails[]) => {
      const newCampaigns = [campaign, ...prev];
      return newCampaigns;
    });
  }, []);

  // react-router location used to avoid loading campaigns on public QR redirect routes
  const location = useLocation();

  // Load data when user changes
  useEffect(() => {
    // If we're serving a public QR redirect route, skip loading the user's full campaign data.
    // Visiting `/qr/:token` should be fast and not require pulling all user campaigns.
    try {
      const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
      // prefer react-router location when available
      // (useLocation below provides the same info and will keep this effect reactive)
      if (location && location.pathname && String(location.pathname).startsWith('/qr/')) {
        return;
      }
      if (path && String(path).startsWith('/qr/')) {
        return;
      }

    } catch (e) {
      // swallow any errors and continue to refreshData as a fallback
      console.warn('CampaignContext: error checking path, continuing to load campaigns', e);
    }

    refreshData();
  }, [user?.id, location?.key]); // location.key changes when pathname, search, or hash changes

  

  const value = useMemo<CampaignContextType>(() => ({
    campaigns,
    qrCodes,
    analytics,
    loading,
    error,
    refreshData,
    addCampaign
  }), [campaigns, qrCodes, analytics, loading, error, refreshData, addCampaign]);

  return (
    <CampaignContext.Provider value={value}>
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignProvider;
